import React from 'react';

const Step2ExistingPatient = React.memo(function Step2ExistingPatient({
  existingPatientData,
  handleExistingPatientChange,
  handleExistingPatientSearch,
  loading,
  debugMode,
  existingPatientResult,
  zipcodeData,
  visitTypes,
  selectedVisitType,
  handleVisitTypeChange,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Your Patient Record</h2>
        <p className="text-gray-600">Please provide your information to locate your existing patient record</p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
        <div className="flex items-start">
          <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="space-y-1">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> If this is your first time with us, please use the "New Patient" form to ensure your appointment is properly scheduled. For questions, call <span className="font-semibold">(530) 806-0805</span>.
            </p>
            <p className="text-xs text-blue-700">
              <strong>Search tip:</strong> Enter the name and date of birth <em>exactly</em> as they appear on insurance records — this is an exact match search. Nicknames (e.g., "Bob" vs "Robert") or middle names in the first name field will not match.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleExistingPatientSearch} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input
              type="text"
              name="first_name"
              value={existingPatientData.first_name}
              onChange={handleExistingPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={existingPatientData.last_name}
              onChange={handleExistingPatientChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
          <input
            type="date"
            name="dob"
            value={(() => {
              // State stores MM/DD/YYYY; date input needs YYYY-MM-DD
              if (existingPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(existingPatientData.dob)) {
                const [m, d, y] = existingPatientData.dob.split('/');
                return `${y}-${m}-${d}`;
              }
              return existingPatientData.dob || '';
            })()}
            onChange={handleExistingPatientChange}
            required
            max={new Date().toISOString().split('T')[0]}
            min="1900-01-01"
            className={`w-full px-3 py-2 border rounded-md focus:ring-orange-500 focus:border-orange-500 ${
              existingPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(existingPatientData.dob)
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300'
            }`}
          />
          {existingPatientData.dob && /^\d{2}\/\d{2}\/\d{4}$/.test(existingPatientData.dob) && (
            <p className="mt-1 text-xs text-green-600 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Date selected: {existingPatientData.dob}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">Enter the patient's date of birth exactly as it appears on their insurance record</p>
        </div>

        {debugMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Visit Type *</label>
            <select
              value={selectedVisitType}
              onChange={handleVisitTypeChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select a visit type...</option>
              {visitTypes.map((visitType) => (
                <option key={visitType.id} value={visitType.id}>
                  {visitType.name} ({visitType.duration} min) {visitType.isDefault ? '(Default)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This determines the type of appointment and may affect available time slots
            </p>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center"
          >
            {loading ? 'Searching...' : 'Search Patient'}
          </button>
        </div>
      </form>

      {debugMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg">
          <details className="group">
            <summary className="cursor-pointer p-4 hover:bg-blue-100 transition-colors">
              <h4 className="font-semibold text-blue-800 inline-flex items-center">
                🔍 Debug: Search Data & Results
                <svg className="w-4 h-4 ml-2 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </h4>
            </summary>
            <div className="px-4 pb-4">
              <div className="space-y-2">
                <div>
                  <strong>Search Data (MM/DD/YYYY format):</strong>
                  <pre className="text-xs text-blue-700 overflow-x-auto">
                    {JSON.stringify(existingPatientData, null, 2)}
                  </pre>
                  <div className="text-xs text-green-600 mt-1">
                    ✓ Date manually entered in MM/DD/YYYY format for API compatibility
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    🩺 Visit Type: {selectedVisitType ? `${visitTypes.find(vt => vt.id.toString() === selectedVisitType)?.name} (ID: ${selectedVisitType})` : '❌ Not selected'}
                  </div>
                </div>
                {existingPatientResult && (
                  <div>
                    <strong>Search Result:</strong>
                    <pre className="text-xs text-blue-700 overflow-x-auto">
                      {JSON.stringify(existingPatientResult, null, 2)}
                    </pre>
                  </div>
                )}
                {zipcodeData && (
                  <div>
                    <strong>ZIP Code Data:</strong>
                    <pre className="text-xs text-blue-700 overflow-x-auto">
                      {JSON.stringify(zipcodeData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
});

export default Step2ExistingPatient;
